import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { Compensations, CompensationTable, InjectionMap } from 'src/app/model/response/file-map-response';
import { loadAxisLabel, orientInjectionGrid, orientNativeCurve } from 'src/app/model/injection-map-orientation';
import { TruncNumberPipe } from 'src/app/share/pipes/trunc-number.pipe';
import { mapAxisText } from 'src/app/model/injection-map-format';
import { gridRange, heatmapCellStyle } from '../heatmap';
import { MapArtifactPayload, CorrectionArtifactPayload } from 'src/app/model/response/ai-chat-response';

/** Kinds e identificadores de eixo com tradução em injectionMap.*; os demais aparecem crus. */
const KNOWN_KINDS = ['engTemp', 'airTemp', 'tps', 'map', 'rpm', 'vBat', 'cylinder_trim', 'rotor_trim', 'gearChange', 'gearBased', 'injector_linearity'];
const KNOWN_AXES = ['engineTemp', 'rpm', 'load', 'time', 'cylinder'];

@Component({
  selector: 'app-map-artifact',
  standalone: true,
  imports: [CommonModule, TruncNumberPipe],
  template: `
    <section class="map-artifact" *ngIf="map">
      <h3>{{ t('injectionMap.title') }} <small *ngIf="map.file_name">{{ map.file_name }}</small></h3>
      <div *ngFor="let bank of map.maps; let b = index" class="map-block">
        <h4>{{ t('injectionMap.bank', { bank: bank.bank }) }} · {{ loadAxisLabel(bank.axis) }} · {{ t('injectionMap.injectionTimeMs') }}</h4>
        <p *ngIf="trimPercent(bank) as percent">{{ t('injectionMap.overallTrim', { percent: (percent | truncNumber:'1.0-2') }) }}</p>
        <ng-container *ngIf="curveGrid(bank) as curve">
          <h5>{{ t('injectionMap.nativeCurve') }} · {{ t('injectionMap.nativeCurvePoints', { count: bank.native_curve?.load?.length ?? 0 }) }}</h5>
          <ng-container *ngTemplateOutlet="mapTable; context: { key: 'curve-' + b, view: curve, corner: loadAxisLabel(bank.axis), structText: 'ms' }"></ng-container>
        </ng-container>
        <ng-container *ngIf="bank.derivation">
          <h5>{{ t('injectionMap.derivedGrid') }}</h5>
          <p>{{ t('injectionMap.derivedGridNote') }}</p>
        </ng-container>
        <ng-container *ngIf="grid(bank) as view">
          <ng-container *ngTemplateOutlet="mapTable; context: { key: 'map-' + b, view: view, corner: axisLabel(view.rowIs, bank.axis) + ' / ' + axisLabel(view.colIs, bank.axis), structText: '' }"></ng-container>
        </ng-container>
      </div>
      <ng-container *ngIf="map.compensations as comp">
        <h4>{{ t('injectionMap.compensations') }}</h4>
        <ng-container *ngFor="let table of comp.tables">
          <div class="map-block" *ngIf="table.enabled || table.included_in_main_map === true">
            <h5>{{ kindLabel(table.kind) }}<span *ngIf="table.bank"> · {{ t('injectionMap.bank', { bank: table.bank }) }}</span> · {{ cellUnitLabel(table) }}</h5>
            <p *ngIf="appliesTo(table) as banks">{{ t('injectionMap.appliesToBanks', { banks: banks }) }}</p>
            <p *ngIf="table.included_in_main_map === true">{{ t('injectionMap.includedInMainMap') }}</p>
            <p *ngIf="!table.enabled">{{ t('injectionMap.disabledInAdjust') }}</p>
            <ng-container *ngIf="compGrid(table) as view">
              <ng-container *ngTemplateOutlet="mapTable; context: { key: 'comp-' + table.key, view: view, corner: view.corner, structText: '' }"></ng-container>
            </ng-container>
          </div>
        </ng-container>
        <p *ngIf="disabled(comp).length">{{ t('injectionMap.disabledList', { list: disabled(comp).join(', ') }) }}</p>
        <div *ngIf="comp.diff_pressure as pressure">
          <p>{{ t(pressure.enabled ? 'injectionMap.diffPressure.active' : 'injectionMap.diffPressure.inactive') }}<span *ngIf="pressure.ref_diff_pressure != null"> · {{ t('injectionMap.diffPressure.reference', { value: pressureText(pressure.ref_diff_pressure) }) }}</span><span *ngIf="pressure.ref_di_pressure != null"> · {{ t('injectionMap.diffPressure.directInjection', { value: pressureText(pressure.ref_di_pressure) }) }}</span></p>
          <p *ngFor="let bank of pressure.banks">{{ t('injectionMap.bank', { bank: bank.bank }) }}: {{ t(bank.compensation ? 'injectionMap.diffPressure.compensationActive' : 'injectionMap.diffPressure.compensationInactive') }}<span *ngIf="bank.pres_ref != null"> · {{ t('injectionMap.diffPressure.bankReference', { value: pressureText(bank.pres_ref) }) }}</span></p>
        </div>
      </ng-container>
      <p *ngFor="let error of errorMessages(map)">{{ error }}</p>
    </section>
    <section class="map-artifact" *ngIf="correction">
      <h3>Correção de mapa <small *ngIf="correction.file_name">{{ correction.file_name }}</small></h3>
      <p>{{ t('injectionMap.bank', { bank: correction.bank }) }} · {{ correction.axis_type }}<span *ngIf="correction.summary"> · {{ correction.summary.cells_covered }}/{{ correction.summary.cells_total }} células cobertas</span></p>
      <div class="table-scroll"><div class="table-frame"><table>
        <thead><tr><th>RPM</th><th>{{ loadAxisLabel(correction.axis_type) }}</th><th>λ alvo</th><th>λ real</th><th>Malha fechada</th><th>Sugestão</th><th>Amostras</th><th>Desvio</th></tr></thead>
        <tbody><tr *ngFor="let cell of correction.cells | slice:0:visibleRows('correction')">
          <td>{{ binText(cell.rpm_lo, cell.rpm_hi) }}</td><td>{{ binText(cell.load_lo, cell.load_hi) }}</td>
          <td>{{ cell.lambda_target | truncNumber:'1.2-2' }}</td><td>{{ cell.lambda_real | truncNumber:'1.2-2' }}</td>
          <td>{{ cell.cl_corr_pct | truncNumber:'1.1-1' }}%</td>
          <td>{{ cell.review ? 'Revisar' : cell.suggested_corr_pct == null ? '—' : ((cell.suggested_corr_pct | truncNumber:'1.1-1') + '%') }}{{ cell.lambda_limited ? ' λ' : '' }}</td>
          <td>{{ cell.n }}</td><td>{{ cell.corr_std | truncNumber:'1.1-1' }}</td>
        </tr></tbody>
      </table>
      <ng-container *ngTemplateOutlet="expandBtn; context: { key: 'correction', rows: correction.cells.length }"></ng-container>
      </div></div>
      <p *ngIf="!correction.cells.length">Nenhuma célula com dados suficientes.</p>
      <p *ngFor="let warning of correction.warnings">{{ warningText(warning) }}</p>
      <p *ngIf="hasLimited(correction)">λ: a malha fechada já aplicava a correção indicada; registre outro log e valide novamente.</p>
    </section>
    <ng-template #mapTable let-key="key" let-view="view" let-corner="corner" let-structText="structText">
      <div class="table-scroll"><div class="table-frame" [attr.data-key]="key" [attr.data-cols]="view.colLabels.length">
        <div class="table-row">
          <table>
            <thead><tr><th>{{ corner }}</th><th *ngFor="let col of visibleColLabels(view.colLabels, key)">{{ view.structCol ? structText : axisText(col) }}</th></tr></thead>
            <tbody><tr *ngFor="let row of visibleGrid(view.grid, key); let i = index"><th>{{ view.structRow ? structText : axisText(view.rowLabels[i]) }}</th><td *ngFor="let value of row | slice:0:visibleCols(key)" [style]="cellStyle(value, view)">{{ value | truncNumber:'1.3-3' }}</td></tr></tbody>
          </table>
          <ng-container *ngTemplateOutlet="expandSideBtn; context: { key: key, cols: view.colLabels.length }"></ng-container>
        </div>
        <ng-container *ngTemplateOutlet="expandBtn; context: { key: key, rows: view.grid.length }"></ng-container>
      </div></div>
    </ng-template>
    <ng-template #expandBtn let-key="key" let-rows="rows">
      <button type="button" class="expand-btn" *ngIf="rows > rowLimit" (click)="toggle(key)"
              [attr.aria-expanded]="isExpanded(key)"
              [attr.title]="isExpanded(key) ? 'Recolher tabela' : 'Exibir todas as ' + rows + ' linhas'">
        <i class="pi" [class.pi-chevron-down]="!isExpanded(key)" [class.pi-chevron-up]="isExpanded(key)"></i>
        <span>{{ isExpanded(key) ? 'Recolher' : 'Expandir' }}</span>
      </button>
    </ng-template>
    <ng-template #expandSideBtn let-key="key" let-cols="cols">
      <button type="button" class="expand-btn expand-btn--side" *ngIf="colFit.has(key)" (click)="toggleCols(key)"
              [attr.aria-expanded]="colsExpanded.has(key)"
              [attr.title]="colsExpanded.has(key) ? 'Recolher colunas' : 'Exibir todas as ' + cols + ' colunas'">
        <i class="pi" [class.pi-chevron-right]="!colsExpanded.has(key)" [class.pi-chevron-left]="colsExpanded.has(key)"></i>
        <span>{{ colsExpanded.has(key) ? 'Recolher' : 'Expandir' }}</span>
      </button>
    </ng-template>
  `,
  styles: [`
    /* block: o ResizeObserver não reporta mudança de tamanho de elemento inline. */
    :host { display: block; }
    .map-artifact { margin: 12px 0; padding: 14px; max-width: 100%; }
    .map-artifact h3, .map-artifact h4, .map-artifact h5 { margin: 0 0 8px; }
    .map-artifact small { margin-left: 8px; font-weight: normal; }
    .map-block { margin: 12px 0; }
    .table-scroll { overflow-x: auto; max-width: 100%; }
    /* inline-block: encolhe até a largura da tabela, e o botão de expandir acompanha. */
    .table-frame { display: inline-block; vertical-align: top; }
    .expand-btn { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; padding: 5px 8px; font: inherit; font-size: 12px; border: 1px solid #26282d; border-top: none; background: rgba(100, 120, 150, .12); color: inherit; cursor: pointer; }
    .expand-btn:hover { background: rgba(100, 120, 150, .24); }
    .expand-btn:focus-visible { outline: 2px solid var(--ftds-accent, #AC281C); outline-offset: -2px; }
    /* Tabela + botão lateral lado a lado; o botão estica até a altura da tabela. */
    .table-row { display: flex; align-items: stretch; }
    .expand-btn--side { width: auto; flex-direction: column; justify-content: center; padding: 8px 5px; border-top: 1px solid #26282d; border-left: none; }
    .expand-btn--side span { writing-mode: vertical-rl; }
    table { border-collapse: collapse; font-variant-numeric: tabular-nums; font-size: 12px; white-space: nowrap; }
    th, td { border: 1px solid #26282d; padding: 5px 8px; text-align: right; }
    th { background: rgba(100, 120, 150, .12); font-weight: 600; }
    p { margin: 8px 0; }
  `],
})
export class MapArtifactComponent implements AfterViewInit, OnDestroy {
  @Input() map?: MapArtifactPayload;
  @Input() correction?: CorrectionArtifactPayload;

  /** Tabelas com mais linhas que isso abrem recolhidas, com botão de expandir embaixo. */
  readonly rowLimit = 12;
  private expanded = new Set<string>();

  isExpanded(key: string): boolean { return this.expanded.has(key); }
  toggle(key: string): void {
    if (this.expanded.has(key)) this.expanded.delete(key);
    else this.expanded.add(key);
  }
  /** Fim do slice das linhas: undefined (todas) quando expandida, senão as primeiras rowLimit. */
  visibleRows(key: string): number | undefined { return this.expanded.has(key) ? undefined : this.rowLimit; }

  /**
   * Linhas visíveis da grade. Existe porque, dentro do ng-template #mapTable, o contexto é `any`
   * e `view.grid | slice` infere `unknown` para a linha; o método devolve `number[][]` tipado.
   */
  visibleGrid(grid: number[][], key: string): number[][] { return grid.slice(0, this.visibleRows(key)); }
  visibleColLabels(labels: number[], key: string): number[] { return labels.slice(0, this.visibleCols(key)); }

  // ─── Colunas: recolhe só quando a tabela não cabe na largura do chat ───────
  /** Quantas colunas de valores cabem, por tabela. Ausente = cabe tudo (sem botão lateral). */
  readonly colFit = new Map<string, number>();
  readonly colsExpanded = new Set<string>();
  /** Larguras medidas na 1ª renderização (todas as colunas visíveis): rótulo + média das colunas de valor. */
  private colWidths = new Map<string, { label: number; col: number }>();
  private resizeObserver?: ResizeObserver;
  private measureFrame = 0;
  /** Reserva para o botão lateral ao calcular quantas colunas cabem. */
  private static readonly SIDE_BTN_PX = 34;

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef, private transloco: TranslocoService) {}

  t(key: string, params?: Record<string, unknown>): string { return this.transloco.translate(key, params); }

  ngAfterViewInit(): void {
    this.measureCols();
    this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
    this.resizeObserver.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.measureFrame);
  }

  toggleCols(key: string): void {
    if (this.colsExpanded.has(key)) this.colsExpanded.delete(key);
    else this.colsExpanded.add(key);
  }

  /** Fim do slice das colunas: undefined (todas) se cabe tudo ou se o usuário expandiu. */
  visibleCols(key: string): number | undefined {
    return this.colsExpanded.has(key) ? undefined : this.colFit.get(key);
  }

  private scheduleMeasure(): void {
    cancelAnimationFrame(this.measureFrame);
    this.measureFrame = requestAnimationFrame(() => this.measureCols());
  }

  private measureCols(): void {
    let changed = false;
    this.host.nativeElement.querySelectorAll<HTMLElement>('.table-frame[data-key]').forEach((frame) => {
      const key = frame.dataset['key']!;
      const total = Number(frame.dataset['cols']);
      const available = frame.parentElement?.clientWidth ?? 0;
      if (!available || !total) return;

      let widths = this.colWidths.get(key);
      if (!widths) {
        const ths = Array.from(frame.querySelectorAll<HTMLElement>('thead th'));
        if (ths.length < 2) return;
        const data = ths.slice(1);
        widths = { label: ths[0].offsetWidth, col: data.reduce((sum, th) => sum + th.offsetWidth, 0) / data.length };
        this.colWidths.set(key, widths);
      }

      const fitsAll = widths.label + total * widths.col <= available;
      const fit = fitsAll ? null : Math.max(1, Math.floor((available - widths.label - MapArtifactComponent.SIDE_BTN_PX) / widths.col));
      if (fit === null || fit >= total) {
        changed = this.colFit.delete(key) || changed;
      } else if (this.colFit.get(key) !== fit) {
        this.colFit.set(key, fit);
        changed = true;
      }
    });
    if (changed) this.cdr.detectChanges();
  }

  axisText(value: number): string { return mapAxisText(value); }
  binText(lo: number, hi: number): string { return `${mapAxisText(lo)}–${mapAxisText(hi)}`; }
  pressureText(value: number): string { return mapAxisText(value); }

  grid(map: InjectionMap) { return this.tableView(orientInjectionGrid(map), false); }
  /** Curva básica: 1 linha × N cargas; a dimensão estrutural (rpm) não tem rótulo. */
  curveGrid(map: InjectionMap) {
    const curve = map.native_curve;
    return curve ? this.tableView(orientNativeCurve({ ...map, native_curve: curve }), true) : null;
  }
  /** Ajuste global já aplicado aos tempos; 0 quando ausente (não exibe nota). */
  trimPercent(map: InjectionMap): number { return map.overall_trim?.percent ?? 0; }
  compGrid(table: CompensationTable) {
    const view = this.tableView(orientInjectionGrid({rpm: table.rows, load: table.cols, values_ms: table.table,
      inverted_axis: table.inverted_axis, inverted_rows: table.inverted_rows,
      inverted_cols: table.inverted_cols}), table.dim === 1);
    const rowAxis = this.axisIdLabel(view.rowIs === 'rpm' ? table.row_axis : table.col_axis);
    const colAxis = this.axisIdLabel(view.colIs === 'load' ? table.col_axis : table.row_axis);
    // Em curvas 1D o eixo estrutural não tem nome: o canto mostra só o eixo real.
    return {...view, corner: view.structRow ? colAxis : view.structCol ? rowAxis : `${rowAxis} / ${colAxis}`};
  }
  /** Mesmo heatmap das tabelas markdown do chat; faixa (min/max) por tabela. */
  cellStyle(value: number, view: { range: { min: number; max: number } | null }): string {
    return view.range && Number.isFinite(value) ? heatmapCellStyle(value, view.range.min, view.range.max) : '';
  }
  /** `structural`: a dimensão 'rpm' é só a forma 1×N da curva; marca qual eixo exibido não imprime rótulo. */
  private tableView<T extends { grid: number[][]; rowIs: 'rpm' | 'load'; colIs: 'rpm' | 'load' }>(view: T, structural: boolean) {
    return { ...view, range: gridRange(view.grid), structRow: structural && view.rowIs === 'rpm', structCol: structural && view.colIs === 'rpm' };
  }
  readonly loadAxisLabel = loadAxisLabel;
  axisLabel(axis: 'rpm' | 'load', loadAxis: string) { return axis === 'rpm' ? 'RPM' : loadAxisLabel(loadAxis); }
  /** Identificador de eixo de compensação; desconhecido aparece cru, null some. */
  axisIdLabel(axis: string | null): string {
    if (!axis) return '';
    return KNOWN_AXES.includes(axis) ? this.t(`injectionMap.axis.${axis}`) : axis;
  }
  kindLabel(kind: string): string { return KNOWN_KINDS.includes(kind) ? this.t(`injectionMap.kind.${kind}`) : kind; }
  /** Rótulo da unidade da célula da compensação. */
  cellUnitLabel(table: CompensationTable): string {
    if (table.units === '%') return this.t('injectionMap.cell.percent');
    if (table.units === 'ms' && table.kind === 'vBat') return this.t('injectionMap.cell.deadtime');
    if (table.units === 'ms' && table.kind === 'injector_linearity') return this.t('injectionMap.cell.additiveMs');
    return table.units;
  }
  appliesTo(table: CompensationTable): string { return (table.applies_to_banks ?? []).join(', '); }
  /** Só as desativadas e fora da grade calculada: as incluídas são exibidas, com nota. */
  disabled(comp: Compensations): string[] {
    return comp.tables.filter(t => !t.enabled && t.included_in_main_map !== true).map(t => this.kindLabel(t.kind) + (t.bank ? ` · ${this.t('injectionMap.bank', { bank: t.bank })}` : ''));
  }
  errorMessages(map: MapArtifactPayload): string[] { return Object.values(map.errors ?? {}).map(e => e?.message).filter((e): e is string => !!e); }
  warningText(warning: { message?: string; warning_code?: string } | string): string { return typeof warning === 'string' ? warning : warning.message ?? warning.warning_code ?? ''; }
  hasLimited(correction: CorrectionArtifactPayload): boolean { return correction.cells.some(c => c.lambda_limited); }
}
